import * as React from 'react';
import { ChevronDown, Home } from 'lucide-react';
import { EmptyState } from '../components/EmptyState.js';
import type { ListingListQuery, ListingRow } from '@proptr/shared';
import {
  HeaderFilter,
  HeaderLabel,
  SortHeader,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type FilterKey = 'all' | 'for-sale' | 'for-rent' | 'archived';
type SortKey = 'totalLeads' | 'price' | 'created' | 'bedrooms' | 'bathrooms';
type SortOrder = 'desc' | 'asc';

const FILTER_OPTIONS: { value: FilterKey; label: string }[] = [
  { value: 'all', label: 'All listings' },
  { value: 'for-sale', label: 'Listings for sale' },
  { value: 'for-rent', label: 'Listings for rent' },
  { value: 'archived', label: 'Listings archived' },
];

function filterToQuery(
  filter: FilterKey,
): Pick<ListingListQuery, 'status' | 'type'> {
  switch (filter) {
    case 'for-sale':
      return { status: 'active', type: 'sale' };
    case 'for-rent':
      return { status: 'active', type: 'rent' };
    case 'archived':
      return { status: 'archived', type: 'all' };
    case 'all':
    default:
      return { status: 'active', type: 'all' };
  }
}

export function ListingsPage() {
  const [filter, setFilter] = React.useState<FilterKey>('all');
  const [sortBy, setSortBy] = React.useState<SortKey>('totalLeads');
  const [order, setOrder] = React.useState<SortOrder>('desc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [items, setItems] = React.useState<ListingRow[] | null>(null);
  const [meta, setMeta] = React.useState<{
    total: number;
    active: number;
    sale: number;
    rent: number;
    archived: number;
    pages: number;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.listings
      .list({
        ...filterToQuery(filter),
        sort: sortBy,
        order,
        page,
        limit: pageSize,
      })
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
        setMeta({
          total: res.meta.total,
          active: res.meta.active,
          sale: res.meta.sale,
          rent: res.meta.rent,
          archived: res.meta.archived,
          pages: res.meta.pages,
        });
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [filter, sortBy, order, page, pageSize]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('desc');
    }
  };

  // Row counts per filter option, shown as tallies in the dropdown.
  // `all` is active listings (the default view); archived lives in its
  // own collection and is counted separately by the API.
  const filterCounts: Record<FilterKey, number | undefined> = {
    all: meta?.active,
    'for-sale': meta?.sale,
    'for-rent': meta?.rent,
    archived: meta?.archived,
  };

  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        {/* Page header */}
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Listings
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            <span className="text-foreground">
              {meta ? meta.active + meta.archived : '—'}
            </span>{' '}
            listings in portfolio
            {meta && (
              <>
                {' · '}
                <span className="text-foreground">{meta.active}</span> active
                {' · '}
                <span className="text-foreground">{meta.archived}</span> archived
              </>
            )}
          </p>
        </div>

        {/* Listings card */}
        <TableCard
          minWidth={1120}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
                {/* min-w folds in the rows' chevron+avatar columns
                    (32+40+two 16px gaps) so labels still align while the
                    filter chip sits flush left on every page. */}
                <HeaderFilter
                  className="min-w-[280px] flex-[3_1_280px] justify-start"
                  value={filter}
                  options={FILTER_OPTIONS.map((opt) => ({
                    ...opt,
                    count: filterCounts[opt.value],
                  }))}
                  onChange={(v) => {
                    setFilter(v);
                    setPage(1);
                  }}
                />
                <HeaderLabel className="min-w-[152px] flex-[2.1_1_152px] justify-start">
                  Agent
                </HeaderLabel>
                <HeaderLabel className="min-w-[86px] flex-[1_1_86px] justify-start">
                  Type
                </HeaderLabel>
                <HeaderLabel className="min-w-[92px] flex-[1.2_1_92px] justify-start">
                  Property
                </HeaderLabel>
                <SortHeader
                  label="Beds"
                  sortKey="bedrooms"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[58px] flex-[0.8_1_58px] justify-start"
                />
                <SortHeader
                  label="Baths"
                  sortKey="bathrooms"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[58px] flex-[0.8_1_58px] justify-start"
                />
                <SortHeader
                  label="Price"
                  sortKey="price"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[116px] flex-[1.5_1_116px] justify-start"
                />
                <SortHeader
                  label="Leads"
                  sortKey="totalLeads"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[54px] flex-[0.7_1_54px] justify-start"
                />
                <SortHeader
                  label="Created"
                  sortKey="created"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[92px] flex-[1.2_1_92px] justify-start"
                />
          </TableHeaderRow>

              {error ? (
                <TableMessage tone="destructive">{error}</TableMessage>
              ) : items === null ? (
                <TableLoading />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={Home}
                  title="No listings found"
                  description="No listings match the selected filters. Once your CRM is connected, your inventory syncs here automatically."
                />
              ) : (
                items.map((listing) => (
                  <ListingItemRow key={listing._id} listing={listing} />
                ))
              )}
        </TableCard>
      </div>
    </AppShell>
  );
}

function ListingItemRow({ listing }: { listing: ListingRow }) {
  const [open, setOpen] = React.useState(false);
  const price =
    typeof listing.price === 'number'
      ? `${listing.price.toLocaleString()} ${listing.currency ?? ''}`.trim()
      : '—';
  const created =
    listing.internalCreated && typeof listing.internalCreated === 'string'
      ? listing.internalCreated.slice(0, 10)
      : '—';
  return (
    <div className="border-b border-[hsl(var(--hairline))] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-transform',
            open && 'rotate-180 bg-muted text-foreground',
          )}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </span>
        <Thumbnail image={listing.image ?? null} />
        <div className="min-w-[176px] flex-[3_1_176px] justify-start">
          <div className="truncate font-sans text-[14px] font-medium text-foreground">
            {listing.title || 'Untitled listing'}
          </div>
          <div className="mono-label text-muted-foreground">
            {listing.externalId ? `#${listing.externalId}` : '—'}
          </div>
        </div>
        <AgentCell agent={listing.agent ?? null} />
        <div className="flex min-w-[86px] flex-[1_1_86px] justify-start">
          <AdTypePill forSale={listing.forSale} forRent={listing.forRent} />
        </div>
        <Cell
          value={listing.propertyType || '—'}
          className="min-w-[92px] flex-[1.2_1_92px] justify-start"
          dim={!listing.propertyType}
        />
        <Cell
          value={
            typeof listing.bedrooms === 'number' ? String(listing.bedrooms) : '—'
          }
          className="min-w-[58px] flex-[0.8_1_58px] justify-start"
        />
        <Cell
          value={
            typeof listing.bathrooms === 'number'
              ? String(listing.bathrooms)
              : '—'
          }
          className="min-w-[58px] flex-[0.8_1_58px] justify-start"
        />
        <Cell value={price} className="min-w-[116px] flex-[1.5_1_116px] justify-start" />
        <Cell
          value={String(listing.totalLeads)}
          className="min-w-[54px] flex-[0.7_1_54px] justify-start"
          dim={listing.totalLeads === 0}
        />
        <Cell value={created} className="min-w-[92px] flex-[1.2_1_92px] justify-start" />
      </button>

      {open && <ListingDetail listing={listing} />}
    </div>
  );
}

// For sale / For rent, styled like the other status pills in the app.
function AdTypePill({
  forSale,
  forRent,
}: {
  forSale?: boolean;
  forRent?: boolean;
}) {
  if (!forSale && !forRent) {
    return (
      <span className="mono-label inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-muted-foreground">
        —
      </span>
    );
  }
  const sale = Boolean(forSale);
  return (
    <span
      className={cn(
        'mono-label inline-flex items-center rounded-full border px-2 py-0.5',
        sale
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700',
      )}
    >
      {sale ? 'For sale' : 'For rent'}
    </span>
  );
}

function AgentCell({
  agent,
}: {
  agent: { name?: string | null; email?: string | null; image?: string | null } | null;
}) {
  if (!agent) {
    return (
      <div className="flex min-w-[152px] flex-[2.1_1_152px] items-center gap-2">
        <div className="h-7 w-7 shrink-0 rounded-full bg-muted" />
        <div className="mono-label text-muted-foreground">Unassigned</div>
      </div>
    );
  }
  const name = agent.name || agent.email || '—';
  return (
    <div className="flex min-w-[152px] flex-[2.1_1_152px] items-center gap-2">
      {agent.image ? (
        <img
          src={agent.image}
          alt={name}
          className="h-7 w-7 shrink-0 rounded-full bg-muted object-cover"
        />
      ) : (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[9px] font-bold uppercase">
          {initials(name || '?')}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-sans text-[12px] font-medium text-foreground">
          {agent.name || '—'}
        </div>
        <div className="truncate font-mono text-[10px] text-muted-foreground">
          {agent.email || '—'}
        </div>
      </div>
    </div>
  );
}

function Thumbnail({ image }: { image: string | null }) {
  if (image) {
    return (
      <img
        src={image}
        alt=""
        className="h-10 w-10 shrink-0 rounded-md bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
      <Home className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function Cell({
  value,
  className,
  dim,
}: {
  value: string;
  className?: string;
  dim?: boolean;
}) {
  return (
    <div className={cn('text-left', className)}>
      <div
        className={cn(
          'truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80',
          dim && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ListingDetail({ listing }: { listing: ListingRow }) {
  const price =
    typeof listing.price === 'number'
      ? `${listing.price.toLocaleString()} ${listing.currency ?? ''}`.trim()
      : '—';
  const saleRent = listing.forSale
    ? 'For sale'
    : listing.forRent
      ? 'For rent'
      : '—';
  return (
    <div className="border-t border-[hsl(var(--hairline))] bg-muted/40 px-5 py-5">
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-4">
        <DetailRow label="Type" value={saleRent} />
        <DetailRow label="Status" value={listing.listingStatus || '—'} />
        <DetailRow label="Property" value={listing.propertyType || '—'} />
        <DetailRow label="Area" value={listing.area || listing.city || '—'} />
        <DetailRow label="Reference" value={listing.externalId || '—'} />
        <DetailRow label="Permit" value={listing.permitId || '—'} />
        <DetailRow label="Price" value={price} />
        <DetailRow
          label="Leads"
          value={String(listing.totalLeads)}
          highlight={listing.totalLeads > 0}
        />
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="mono-label text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-1 truncate font-mono text-[13px] font-semibold tabular-nums',
          highlight && 'text-primary',
        )}
      >
        {value}
      </div>
    </div>
  );
}
